import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Lock, User as UserIcon, Phone, Loader2 } from "lucide-react";
import http from "@/api";
import { toLatinDigits } from "@/lib/digits";
import { useApp } from "@/context";
import DemoAccountsPanel from "@/components/DemoAccountsPanel";
import PhoneField from "@/components/PhoneField";
import { isNativeIOS, isNativePlatform } from "@/utils/platform";

// Demo-account passwords for quick login on /login (only rendered when
// REACT_APP_SHOW_DEMO_LOGIN === "true"). These match seeded test accounts in
// /app/memory/test_credentials.md and are safe to expose because the demo block
// is hidden in production builds. Override via env vars if you reseed.
const DEMO_ADMIN_PASS = process.env.REACT_APP_DEMO_ADMIN_PASS || "";
const DEMO_CUSTOMER_PASS = process.env.REACT_APP_DEMO_CUSTOMER_PASS || "";
const DEMO_STAFF_PASS = process.env.REACT_APP_DEMO_STAFF_PASS || "";
const pickDemoPass = (email) =>
  email.includes("admin") ? DEMO_ADMIN_PASS
    : email.includes("demo") ? DEMO_CUSTOMER_PASS
      : DEMO_STAFF_PASS;

function GoogleIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.5 16 18.9 13 24 13c3 0 5.8 1.1 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.6 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.3l-6.2-5.2c-2 1.4-4.5 2.2-7.2 2.2-5.3 0-9.7-3.4-11.3-8L6.1 32c3.3 6.5 10.1 11 17.9 11z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2C40.8 35.3 44 29.9 44 24c0-1.2-.1-2.4-.4-3.5z" />
    </svg>
  );
}

export function Login() {
  const { t, lang, login, otpSend, otpVerifyLogin } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const nextPath = new URLSearchParams(loc.search).get("next") || "/";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [branches, setBranches] = useState([]);
  const [countries, setCountries] = useState([]);
  const [showBranches, setShowBranches] = useState(false);
  const [branchFilter, setBranchFilter] = useState("ALL");

  // WhatsApp OTP state
  const [waOpen, setWaOpen] = useState(true);  // primary flow — expanded by default
  const [emailOpen, setEmailOpen] = useState(true);  // email/password login expanded by default (B2C friendly)
  const [otpMode, setOtpMode] = useState("phone"); // 'phone' | 'email'
  const [emailCap, setEmailCap] = useState(false); // true once /wa/config reports email_enabled
  const [waPhone, setWaPhone] = useState("+974");
  const [waName, setWaName] = useState("");
  const [waCode, setWaCode] = useState("");
  const [waStage, setWaStage] = useState("phone"); // 'phone' | 'code'
  const [waBusy, setWaBusy] = useState(false);
  const [waMsg, setWaMsg] = useState("");
  const [waEmail, setWaEmail] = useState("");
  // True once a Services ID is configured for WEB Sign in with Apple. Until
  // then the Apple button stays hidden on the website (native iOS always shows).
  const [appleWebConfigured, setAppleWebConfigured] = useState(false);
  // True when a custom (RARE GROUP-owned) Google OAuth client is configured on the
  // backend. When false, the Google button falls back to Emergent-managed auth.
  const [googleConfigured, setGoogleConfigured] = useState(false);

  useEffect(() => {
    // Fetch public branches + countries once
    Promise.all([
      http.get("/branches"),
      http.get("/countries"),
    ]).then(([{ data: b }, { data: c }]) => {
      setBranches(b);
      setCountries(c);
    }).catch(() => {});
    // Probe which messaging channels are available to decide the OTP mode
    http.get("/wa/config").then(({ data }) => {
      setEmailCap(!!data?.email_enabled);
      // Prefer email when SMS/WA is disabled
      if (data?.email_enabled && !data?.sms_or_whatsapp_enabled) setOtpMode("email");
    }).catch(() => {});
    // Show the Apple button on the website only when a web Services ID is set up
    http.get("/auth/apple/status").then(({ data }) => {
      setAppleWebConfigured(!!data?.web_configured);
    }).catch(() => {});
    // Use our own RARE GROUP Google login when configured (no Emergent branding)
    http.get("/auth/google/status").then(({ data }) => {
      setGoogleConfigured(!!data?.configured);
    }).catch(() => {});

    // Iter#44 — surface Apple OAuth errors that came back via ?apple_error=…
    // The most common ones are `invalid_request` (Return URL not registered
    // on Apple Developer Console) and `user_cancelled_authorize`.
    if (typeof window !== "undefined") {
      const sp = new URLSearchParams(window.location.search);
      const ae = sp.get("apple_error");
      if (ae) {
        const msg = ae === "user_cancelled_authorize"
          ? (lang === "ar" ? "تمّ إلغاء تسجيل الدخول من Apple." : "Apple Sign-In cancelled.")
          : (lang === "ar"
              ? `تعذّر تسجيل الدخول عبر Apple (${ae}). تأكّد من تسجيل عنوان الـ Return URL على Apple Developer Console.`
              : `Apple Sign-In failed (${ae}). Make sure the Return URL is registered on Apple Developer Console.`);
        setErr(msg);
        // Clean the URL so a refresh stays clean
        const url = new URL(window.location.href);
        url.searchParams.delete("apple_error");
        window.history.replaceState({}, "", url.pathname + (url.search || "") + url.hash);
      }
    }
  }, [lang]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr(""); setLoading(true);
    try {
      const user = await login(form.email, form.password);
      if (["admin", "country_admin", "branch_admin", "super_admin"].includes(user.role)) nav("/admin");
      else if (["receiver", "cashier"].includes(user.role)) nav("/intake");
      else if (user.role === "employee" || user.role === "staff") nav("/ops");
      else nav(nextPath);
    } catch (e) { setErr(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const demoLogin = async (email) => {
    const password = pickDemoPass(email);
    setForm({ email, password });
    setLoading(true);
    try {
      const user = await login(email, password);
      if (["admin", "country_admin", "branch_admin", "super_admin"].includes(user.role)) nav("/admin");
      else if (["receiver", "cashier"].includes(user.role)) nav("/intake");
      else if (user.role === "employee" || user.role === "staff") nav("/ops");
      else nav(nextPath);
    } catch (e) { setErr(String(e)); }
    finally { setLoading(false); }
  };

  const branchLogin = async (branch) => {
    const slug = (branch.city_en || "").toLowerCase().replace(/\s+/g, "_");
    const email = `branch_${slug}@rg.com`;
    setLoading(true);
    try {
      const user = await login(email, DEMO_ADMIN_PASS);
      nav(["admin", "country_admin", "branch_admin"].includes(user.role) ? "/admin" : nextPath);
    } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
    finally { setLoading(false); }
  };

  // Native (iOS/Android) OAuth via in-app browser + custom-scheme deep-link
  // return. The backend redirects to `info.rgpro.app://auth?token=<jwt>` which
  // the @capacitor/app plugin catches here. Web builds never reach this (it is
  // only called behind isNativePlatform()).
  const startNativeOAuth = async (loginUrl) => {
    setLoading(true);
    try {
      const [{ Browser }, { App }, api] = await Promise.all([
        import("@capacitor/browser"),
        import("@capacitor/app"),
        import("@/api"),
      ]);
      const extractToken = (u) => {
        try { return new URL(u).searchParams.get("token"); }
        catch { const m = /[?&]token=([^&]+)/.exec(u || ""); return m ? decodeURIComponent(m[1]) : null; }
      };
      let settled = false;
      const handle = await App.addListener("appUrlOpen", async ({ url }) => {
        const token = extractToken(url);
        if (!token || settled) return;
        settled = true;
        api.setAuthToken(token);
        try { await Browser.close(); } catch { /* ignore */ }
        try { handle.remove(); } catch { /* ignore */ }
        window.location.href = "/?social=ok";
      });
      await Browser.open({ url: loginUrl, presentationStyle: "popover" });
    } catch (e) {
      setLoading(false);
      setErr(lang === "ar" ? "تعذّر فتح تسجيل الدخول عبر المتصفّح." : "Couldn't open the in-app sign-in.");
    }
  };

  const social = async (provider) => {
    setErr("");
    if (provider === "google") {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      if (googleConfigured) {
        const base = window.location.origin + "/api/auth/google/login";
        // Native app (iOS/Android): open the sign-in inside an in-app browser
        // and return via a custom-scheme deep link so the customer never
        // "leaves" the app to Safari. Google blocks OAuth in embedded WebViews,
        // so a full in-WebView redirect isn't allowed anyway. Web builds skip
        // this entirely and keep the normal top-level redirect below.
        if (isNativePlatform()) { await startNativeOAuth(base + "?native=1"); return; }
        // Custom RARE GROUP-owned Google OAuth — customers see only RARE GROUP, no Emergent branding.
        window.location.href = base;
        return;
      }
      // Fallback: Emergent-managed Google Auth (until the custom client is configured).
      const redirectUrl = window.location.origin + "/";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (provider === "apple") {
      // Iter#60 (App Store re-submission) — when we're running inside the
      // Capacitor iOS WebView, use the NATIVE ASAuthorizationAppleIDProvider
      // flow via @capacitor-community/apple-sign-in. This is what Apple App
      // Review expects for native iOS apps — the web OAuth flow inside a
      // WebView caused the previous rejection.
      if (isNativeIOS()) {
        try {
          setLoading(true);
          const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
          // Generate a random nonce on the client; backend re-checks it.
          const nonce = (typeof crypto !== "undefined" && crypto.randomUUID)
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2);
          const options = {
            clientId: "info.rgpro.app",
            // redirectURI is required by the plugin but the native flow
            // doesn't actually navigate to it — it's a formality.
            redirectURI: "https://rgpro.info/auth/apple/native",
            scopes: "email name",
            state: "rgpro-native",
            nonce,
          };
          const result = await SignInWithApple.authorize(options);
          // Plugin v6 returns { response: { identityToken, authorizationCode, givenName, familyName, ... } }
          const r = result?.response || result || {};
          const identity_token = r.identityToken;
          const authorization_code = r.authorizationCode;
          if (!identity_token) {
            throw new Error(
              "Apple returned no identityToken. Keys received: " +
                Object.keys(r || {}).join(",")
            );
          }
          const givenName = r.givenName || (r.user?.fullName?.givenName) || "";
          const familyName = r.familyName || (r.user?.fullName?.familyName) || "";
          const name = `${givenName} ${familyName}`.trim() || undefined;
          // Send to backend, set the cookie, hydrate the SPA state.
          const { data } = await http.post("/auth/apple/native", {
            identity_token, authorization_code, name, nonce,
          });
          const { setAuthToken } = await import("@/api");
          setAuthToken(data.token || null);
          // Mirror the email/OTP flow: full reload so AppContext re-fetches.
          window.location.href = "/?apple=ok";
          return;
        } catch (e) {
          setLoading(false);
          // Apple plugin throws { code: "1001", message: "..." } when the
          // user cancels. Don't show that as an error — just close.
          const code = e?.code || e?.errorCode || "";
          const msg = e?.response?.data?.detail || e?.message || String(e);
          console.error("[apple-native] ERROR", { code, msg, error: e });
          if (String(msg).toLowerCase().includes("cancel") || code === "1001") return;
          // Show the FULL error to the user so they can screenshot it and
          // send it to us — App Store review needs clear failure messages.
          setErr(
            lang === "ar"
              ? `تعذّر تسجيل الدخول عبر Apple.\nالسبب: ${msg}${code ? `\n(code: ${code})` : ""}`
              : `Apple Sign-In failed.\nReason: ${msg}${code ? `\n(code: ${code})` : ""}`
          );
          return;
        }
      }
      // Non-iOS (browser, Android) — fall back to the web OAuth flow.
      // Iter#56 — use window.location.origin directly so the redirect
      // works on preview, production, and any custom domain. The /api
      // prefix is auto-routed to the backend by Kubernetes ingress.
      window.location.href = window.location.origin + "/api/auth/apple/login";
      return;
    }
  };

  const waStart = async () => {
    setWaMsg(""); setErr("");
    // Iter#56 — name is now required so admin/dashboard always shows a real
    // human name, not the phone number / placeholder email.
    const cleanName = (waName || "").trim();
    if (cleanName.length < 2) {
      setWaMsg(lang === "ar"
        ? "يرجى إدخال اسمك أولاً (حرفان على الأقل)"
        : "Please enter your name (at least 2 characters)");
      return;
    }
    if (otpMode === "email") {
      if (!waEmail || !/\S+@\S+\.\S+/.test(waEmail)) {
        setWaMsg(lang === "ar" ? "أدخل بريداً صحيحاً" : "Enter a valid email");
        return;
      }
      setWaBusy(true);
      try {
        await http.post("/auth/email/send-otp", { email: waEmail });
        setWaStage("code");
        setWaMsg(lang === "ar"
          ? "تم إرسال رمز مكوّن من 6 أرقام إلى بريدك"
          : "A 6-digit code was sent to your email");
      } catch (e) { setWaMsg(e?.response?.data?.detail || String(e)); }
      finally { setWaBusy(false); }
      return;
    }
    if (!waPhone || waPhone.length < 8) {
      setWaMsg(lang === "ar" ? "أدخل رقم جوال صحيح" : "Enter a valid phone number");
      return;
    }
    setWaBusy(true);
    try {
      await otpSend(waPhone);
      setWaStage("code");
      setWaMsg(lang === "ar"
        ? "تم إرسال رمز مكوّن من 6 أرقام عبر واتساب/SMS"
        : "A 6-digit code was sent via WhatsApp/SMS");
    } catch (e) {
      setWaMsg(e?.response?.data?.detail || String(e));
    } finally { setWaBusy(false); }
  };

  const waFinish = async () => {
    if (!/^\d{6}$/.test(waCode)) {
      setWaMsg(lang === "ar" ? "الرمز 6 أرقام" : "Code must be 6 digits");
      return;
    }
    setWaBusy(true);
    try {
      let user;
      if (otpMode === "email") {
        // Cookie is set by the backend; we also stash the in-memory token
        // via setAuthToken so cross-origin (rgpro.info → emergent backend)
        // checkout calls keep working when the browser drops the cookie.
        const { data } = await http.post("/auth/email/verify-otp",
          { email: waEmail, otp: waCode, name: waName });
        user = data.user;
        const { setAuthToken } = await import("@/api");
        setAuthToken(data.token || null);
        window.location.reload();
      } else {
        user = await otpVerifyLogin(waPhone, waCode, waName);
      }
      if (user) nav(["admin","country_admin","branch_admin"].includes(user.role) ? "/admin" : nextPath);
    } catch (e) {
      setWaMsg(e?.response?.data?.detail || String(e));
    } finally { setWaBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-8">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-6">
          <img src="/brand/logo-transparent.png" alt="RARE GROUP — Luxury Flowers & Gifts"
               className="h-12 w-auto mx-auto mb-3" data-testid="auth-logo" />
          {nextPath === "/checkout" ? (
            <>
              <h1 className={`${lang === "ar" ? "font-display-ar" : "font-display-en"} text-3xl`}>
                {lang === "ar" ? "على وشك إرسال هديتك 🌹" : "Almost there 🌹"}
              </h1>
              <p className="opacity-80 mt-2 text-sm">
                {lang === "ar"
                  ? "سجّل دخولاً سريعاً لإتمام طلبك — 10 ثوانٍ فقط."
                  : "Quick sign-in to complete your order — just 10 seconds."}
              </p>
            </>
          ) : (
            <>
              <h1 className={`${lang === "ar" ? "font-display-ar" : "font-display-en"} text-3xl`}>{t("welcomeBack")}</h1>
              <p className="opacity-70 mt-1 text-sm">{t("appTagline")}</p>
            </>
          )}
        </div>

        <div className="card-soft p-5 space-y-3">
          {/* ===== PRIMARY: WhatsApp OTP (hero) ===== */}
          <div className="rounded-2xl p-4 space-y-3"
               style={{ background: "linear-gradient(135deg, rgba(37,211,102,0.12), rgba(18,140,126,0.08))",
                        border: "2px solid #25D366" }}
               data-testid="wa-hero-panel">
            <div className="flex items-center gap-2 justify-center">
              <Phone size={20} style={{ color: "#128C7E" }} />
              <div className={`${lang === "ar" ? "font-display-ar" : "font-display-en"} text-xl font-semibold`} style={{ color: "#128C7E" }}>
                {lang === "ar" ? "الدخول السريع" : "Quick sign-in"}
              </div>
            </div>
            <div className="text-xs text-center opacity-70">
              {lang === "ar"
                ? "بدون كلمة مرور — رمز يصلك خلال ثوانٍ"
                : "No password — code delivered in seconds"}
            </div>

            {/* Mode toggle: Phone vs Email */}
            {emailCap && waStage === "phone" && (
              <div className="flex bg-white/60 rounded-lg p-0.5 text-xs">
                <button type="button" onClick={() => setOtpMode("phone")}
                        className={`flex-1 py-1.5 rounded-md transition-all ${otpMode === "phone" ? "bg-white shadow-sm font-semibold" : "opacity-60"}`}
                        data-testid="otp-mode-phone">
                  📱 {lang === "ar" ? "هاتف" : "Phone"}
                </button>
                <button type="button" onClick={() => setOtpMode("email")}
                        className={`flex-1 py-1.5 rounded-md transition-all ${otpMode === "email" ? "bg-white shadow-sm font-semibold" : "opacity-60"}`}
                        data-testid="otp-mode-email">
                  ✉️ {lang === "ar" ? "بريد" : "Email"}
                </button>
              </div>
            )}

            {waStage === "phone" && otpMode === "phone" && (
              <>
                <input className="input-field !bg-white" type="text"
                       placeholder={lang === "ar" ? "اسمك *" : "Your name *"}
                       value={waName} onChange={(e) => setWaName(e.target.value)}
                       required maxLength={60}
                       data-testid="wa-name-input" />
                <input className="input-field !bg-white" type="tel" placeholder="+974…"
                       value={waPhone} onChange={(e) => setWaPhone(e.target.value)}
                       data-testid="wa-phone-input" />
                <button type="button" onClick={waStart} disabled={waBusy}
                        className="w-full rounded-xl py-3 font-semibold text-base text-white flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60"
                        style={{ background: "#25D366", boxShadow: "0 4px 14px rgba(37,211,102,0.35)" }}
                        data-testid="wa-send-code-btn">
                  {waBusy ? <Loader2 className="animate-spin" size={18} /> : <Phone size={18} />}
                  {lang === "ar" ? "أرسل رمز التحقق" : "Send code"}
                </button>
              </>
            )}

            {waStage === "phone" && otpMode === "email" && (
              <>
                <input className="input-field !bg-white" type="text"
                       placeholder={lang === "ar" ? "اسمك *" : "Your name *"}
                       value={waName} onChange={(e) => setWaName(e.target.value)}
                       required maxLength={60}
                       data-testid="wa-name-input" />
                <input className="input-field !bg-white" type="email" placeholder={lang === "ar" ? "بريدك الإلكتروني" : "Your email"}
                       value={waEmail} onChange={(e) => setWaEmail(e.target.value)}
                       data-testid="wa-email-input" />
                <button type="button" onClick={waStart} disabled={waBusy}
                        className="w-full rounded-xl py-3 font-semibold text-base text-white flex items-center justify-center gap-2 transition-all active:scale-[0.98] disabled:opacity-60"
                        style={{ background: "#0891b2", boxShadow: "0 4px 14px rgba(8,145,178,0.35)" }}
                        data-testid="wa-send-email-btn">
                  {waBusy ? <Loader2 className="animate-spin" size={18} /> : null}
                  {lang === "ar" ? "أرسل الرمز للبريد" : "Email me the code"}
                </button>
              </>
            )}
            {waStage === "code" && (
              <>
                <div className="text-xs text-center opacity-80">
                  {lang === "ar" ? `أرسلنا الرمز إلى ${waPhone}` : `Code sent to ${waPhone}`}
                </div>
                <input className="input-field !bg-white font-mono text-center tracking-widest text-xl"
                       type="text" inputMode="numeric" maxLength={6}
                       placeholder="------" value={waCode}
                       onChange={(e) => setWaCode(toLatinDigits(e.target.value).replace(/\D/g, ""))}
                       data-testid="wa-code-input" />
                <div className="flex gap-2">
                  <button type="button" onClick={() => setWaStage("phone")} className="btn-outline flex-1 !py-2.5 text-sm">
                    {lang === "ar" ? "رجوع" : "Back"}
                  </button>
                  <button type="button" onClick={waFinish} disabled={waBusy}
                          className="flex-1 rounded-xl py-2.5 font-semibold text-sm text-white flex items-center justify-center gap-2 disabled:opacity-60"
                          style={{ background: "#25D366" }}
                          data-testid="wa-verify-btn">
                    {waBusy ? <Loader2 className="animate-spin" size={14} /> : null}
                    {lang === "ar" ? "تحقق ودخول" : "Verify & sign in"}
                  </button>
                </div>
              </>
            )}
            {waMsg && <div className="text-xs text-center" style={{ color: "#dc2626" }} data-testid="wa-msg">{waMsg}</div>}
          </div>

          {/* ===== SECONDARY: Google / Apple ===== */}
          <div className="flex items-center gap-3 pt-1">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            <span className="text-[11px] uppercase tracking-widest opacity-50">
              {lang === "ar" ? "أو" : "or"}
            </span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>

          <div className={`grid ${(isNativeIOS() || appleWebConfigured) ? "grid-cols-2" : "grid-cols-1"} gap-2`}>
            {/* Iter#60 (App Store fix) — both social buttons share the SAME
                height, font weight, padding, icon size and label format.
                Apple HIG requires "Sign in with Apple"; we use the same
                pattern for Google so neither button looks more important. */}
            <button type="button" onClick={() => social("google")}
                    className="!py-2.5 text-sm rounded-xl border-2 flex items-center justify-center gap-1.5 font-semibold transition-all active:scale-[0.98] bg-white"
                    style={{ borderColor: "#dadce0", color: "#3c4043", minHeight: "44px" }}
                    data-testid="login-google-btn">
              <GoogleIcon size={16} />
              <span className="truncate">
                {lang === "ar" ? "متابعة بـ Google" : "Continue with Google"}
              </span>
            </button>
            {/* Apple HIG-compliant Sign-In button — shown ONLY inside the
                native iOS app (native ASAuthorization flow). Hidden on the
                website/PWA because web Sign in with Apple requires a Services
                ID + verified domain (not set up), which returned invalid_request. */}
            {(isNativeIOS() || appleWebConfigured) && (
            <button type="button" onClick={() => social("apple")}
                    className="!py-2.5 text-sm rounded-xl flex items-center justify-center gap-1.5 font-semibold transition-all active:scale-[0.98]"
                    style={{ background: "#000", color: "#fff", minHeight: "44px" }}
                    data-testid="login-apple-btn"
                    aria-label={lang === "ar" ? "تسجيل الدخول بـ Apple" : "Sign in with Apple"}>
              <svg width="14" height="16" viewBox="0 0 17 21" fill="currentColor" aria-hidden="true">
                <path d="M13.6 11.18c-.02-2.13 1.74-3.17 1.82-3.22-.99-1.45-2.54-1.65-3.09-1.67-1.31-.13-2.57.77-3.24.77-.68 0-1.71-.76-2.81-.74-1.43.02-2.77.84-3.51 2.12-1.51 2.61-.38 6.46 1.07 8.58.72 1.04 1.56 2.2 2.67 2.16 1.08-.04 1.48-.69 2.78-.69 1.29 0 1.66.69 2.79.66 1.15-.02 1.88-1.05 2.58-2.09.83-1.2 1.16-2.36 1.18-2.42-.03-.01-2.25-.86-2.27-3.42m-2.11-6.29c.59-.72.99-1.71.88-2.7-.85.04-1.89.58-2.5 1.29-.55.63-1.04 1.65-.91 2.62.95.07 1.93-.49 2.53-1.21" />
              </svg>
              <span className="truncate">
                {lang === "ar" ? "متابعة بـ Apple" : "Sign in with Apple"}
              </span>
            </button>
            )}
          </div>

          {/* ===== TERTIARY: Email/Password (collapsed) ===== */}
          <button type="button" onClick={() => setEmailOpen(!emailOpen)}
                  className="w-full text-center text-xs opacity-60 hover:opacity-100 transition-opacity pt-1"
                  data-testid="toggle-email-login">
            {lang === "ar"
              ? (emailOpen ? "إخفاء تسجيل الدخول بالبريد" : "تسجيل الدخول بالبريد الإلكتروني بدلاً من ذلك")
              : (emailOpen ? "Hide email sign-in" : "Sign in with email instead")}
          </button>

          {emailOpen && (
            <form onSubmit={submit} className="space-y-3 pt-1" data-testid="email-login-form">
              <label className="relative block">
                <Mail size={16} className="absolute top-1/2 -translate-y-1/2 opacity-60"
                      style={{ [lang === "ar" ? "right" : "left"]: 14 }} />
                <input className="input-field" type="email" placeholder={t("email")}
                       style={{ [lang === "ar" ? "paddingRight" : "paddingLeft"]: "2.5rem" }}
                       value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       data-testid="login-email-input" required />
              </label>
              <label className="relative block">
                <Lock size={16} className="absolute top-1/2 -translate-y-1/2 opacity-60"
                      style={{ [lang === "ar" ? "right" : "left"]: 14 }} />
                <input className="input-field" type="password" placeholder={t("password")}
                       style={{ [lang === "ar" ? "paddingRight" : "paddingLeft"]: "2.5rem" }}
                       value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                       data-testid="login-password-input" required />
              </label>
              {err && <div className="text-sm text-red-500 whitespace-pre-line" data-testid="login-error">{err}</div>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit-btn">
                {loading ? <Loader2 className="animate-spin" size={16} /> : null} {t("login")}
              </button>
            </form>
          )}
        </div>

        <div className="text-center mt-4 text-sm">
          {t("noAccount")} <Link to="/register" className="font-semibold" style={{ color: "var(--primary)" }} data-testid="login-to-register">{t("register")}</Link>
        </div>

        {/* Dev/test login section — hidden by default in production via env flag.
             Set REACT_APP_SHOW_DEMO_LOGIN=true in /app/frontend/.env to expose
             the quick-login chips and branch-manager dropdown to internal users. */}
        {process.env.REACT_APP_SHOW_DEMO_LOGIN === "true" && (
          <DemoAccountsPanel
            demoLogin={demoLogin}
            branchLogin={branchLogin}
            branches={branches}
            countries={countries}
            branchFilter={branchFilter}
            setBranchFilter={setBranchFilter}
            showBranches={showBranches}
            setShowBranches={setShowBranches}
            loading={loading}
            lang={lang}
            t={t}
            DEMO_ADMIN_PASS={DEMO_ADMIN_PASS}
          />
        )}
      </motion.div>
    </div>
  );
}

export function Register() {
  const { t, lang, register } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const digits = (form.phone || "").replace(/[^0-9]/g, "");
    if (digits.length < 11) {
      setErr(lang === "ar" ? "رقم الجوال إلزامي — أدخل رقماً صحيحاً" : "Mobile number is required — enter a valid number");
      return;
    }
    setLoading(true);
    try { await register(form); nav("/"); }
    catch (e) { setErr(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-8">
      <h1 className={`${lang === "ar" ? "font-display-ar" : "font-display-en"} text-3xl text-center mb-4`}>{t("register")}</h1>
      <form onSubmit={submit} className="card-soft p-5 space-y-3">
        <input className="input-field" placeholder={t("name")} value={form.name}
               onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="reg-name-input" required />
        <input className="input-field" type="email" placeholder={t("email")} value={form.email}
               onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="reg-email-input" required />
        <div>
          <label className="text-xs opacity-70 block mb-1">
            {lang === "ar" ? "رقم الجوال (إلزامي)" : "Mobile number (required)"}
          </label>
          <PhoneField value={form.phone || "+974"} onChange={(v) => setForm({ ...form, phone: v })}
                      lang={lang} testId="reg-phone" />
        </div>
        <input className="input-field" type="password" placeholder={t("password")} value={form.password}
               onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reg-password-input" required />
        {err && <div className="text-sm text-red-500">{err}</div>}
        <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reg-submit-btn">
          {loading ? <Loader2 className="animate-spin" size={16} /> : null} {t("register")}
        </button>
        <div className="text-center text-sm">
          {t("hasAccount")} <Link to="/login" style={{ color: "var(--primary)" }}>{t("login")}</Link>
        </div>
      </form>
    </div>
  );
}
